import {Component, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {Organization} from '../../../../shared/model/organization';
import {ApiResponse} from '../../../../shared/model/api-response';
import {AlertService} from '../../../../shared/services/alert.service';
import {ConfirmDialogService} from '../../../../shared/services/confirm-dialog.service';
import {GenericCardComponent} from '../../../../shared/components/generic-card/generic-card.component';
import {RouterLink} from '@angular/router';
import {PermissionService} from '../../../../core/services/permission.service';
import {OrganizationMembership} from '../../../../shared/model/organization-membership';
import {NgClass} from '@angular/common';
import {Messages} from '../../../../shared/constants/messages';
import {NavigationHistoryService} from '../../../../core/services/navigation-history.service';
import {EventService} from '../../../../shared/services/event.service';
import {OsmMapComponent} from '../../../../shared/components/osm-map/osm-map.component';
import {eventMapMarkers} from '../../../../shared/utils/map-markers';
import {MapMarker} from '../../../../shared/model/map-marker';
import {OrgCapability} from '../../../../shared/model/permissions';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [GenericCardComponent, RouterLink, NgClass, OsmMapComponent],
  templateUrl: './organization-detail.component.html',
  styleUrl: './organization-detail.component.css',
})
export class OrganizationDetailComponent implements OnInit {
  organization = signal<Organization | null>(null);
  members = signal<OrganizationMembership[]>([]);
  isLoadingMembers = signal<boolean>(false);
  eventMarkers = signal<MapMarker[]>([]);
  hasPowerToSeeMembers = signal<boolean>(false);

  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private organizationService: OrganizationService = inject(OrganizationService);
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  private permissionService: PermissionService = inject(PermissionService);
  private navigationHistory: NavigationHistoryService = inject(NavigationHistoryService);
  private eventService: EventService = inject(EventService);

  ngOnInit(): void {
    const orgId = Number(this.route.snapshot.paramMap.get('id'));
    if (orgId) {
      this.loadOrganization(orgId);
      this.loadOrganizationEvents(orgId);
    }
  }

  loadOrganization(id: number) {
    this.organizationService.getOrganizationById(id).subscribe({
      next: (response: ApiResponse<Organization>) => {
        if (response.success && response.data) {
          const org = response.data;
          this.organization.set(org);
          this.hasPowerToSeeMembers.set(this.permissionService.hasOrgCapability(org, OrgCapability.ORG_MEMBER_VIEW));
          if (this.hasPowerToSeeMembers()) {
            this.loadMembers(id);
          }
        }
      },
      error: () => {
        this.alertService.showError("Errore nel caricamento dell'organizzazione");
        this.router.navigate(['/organizations']);
      },
    });
  }

  loadMembers(orgId: number) {
    this.isLoadingMembers.set(true);
    this.organizationService.getOrganizationMembers(orgId).subscribe({
      next: (response: ApiResponse<OrganizationMembership[]>) => {
        if (response.success && response.data) {
          this.members.set(response.data);
        }
        this.isLoadingMembers.set(false);
      },
      error: () => {
        this.isLoadingMembers.set(false);
      },
    });
  }

  private loadOrganizationEvents(orgId: number) {
    this.eventService.getAllEvents(true).subscribe({
      next: (response) => {
        const events = (response.data ?? []).filter(
          (event) => event.organization?.id === orgId && event.active !== false,
        );
        this.eventMarkers.set(eventMapMarkers(events));
      },
      error: () => {
        this.eventMarkers.set([]);
      },
    });
  }

  canOpenEdit(): boolean {
    const org = this.organization();
    return (
      !!org &&
      (this.permissionService.canEditOrg(org) || this.permissionService.canManageOrgMembers(org))
    );
  }

  canDelete(): boolean {
    return this.permissionService.canDeleteOrg(this.organization());
  }

  async onDelete(org: Organization) {
    if (!this.canDelete()) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_ORGANIZATION_DELETE_TITLE,
      message: Messages.confirmOrganizationDelete(org.organizationName),
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.organizationService.deleteOrganization(org.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.showSuccess(Messages.ORGANIZATION_DELETED_SUCCESS);
          this.router.navigate(['/organizations']);
        }
      },
    });
  }

  goBack() {
    this.navigationHistory.goBack(['/organizations']);
  }

  getRoleBadgeClass(role: string): string {
    switch (role?.toUpperCase()) {
      case 'OWNER':
        return 'bg-success';
      case 'ADMIN':
        return 'bg-primary';
      case 'MODERATOR':
        return 'bg-warning';
      default:
        return 'bg-info';
    }
  }
}
